import {Button, darken, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography} from "@mui/material";
import React from "react";
import {cleanCondition, convertToGroup, operators, RecursiveConditionField} from "../../../../inventory/shared/mapping_templates";
import {Field, Form, useForm} from "react-final-form";
import arrayMutators from "final-form-arrays";
import {FieldArray} from "react-final-form-arrays";
import EnhancedSelect from "../../../../../components/EnhancedSelect";
import {Nest} from "../../../../../utils/icons";
import {getErrorMessage} from "../../../../inventory/shared/mapping_checker";
import EnhancedAutocomplete from "../../../../../components/EnhancedAutocomplete";
import {Clear, Delete} from "@mui/icons-material";
import { pageContentGap } from '../../../../../theme';

// region TypeScript Interfaces
interface ConditionOption {
    label: string;
    value: string;
    [key: string]: unknown;
}

interface RecursiveConditionalProps {
    name: string;
    remove?: () => void;
    options: ConditionOption[];
    depth: number;
    mutators: any;
    noOptions?: boolean;
    actions?: React.ReactNode;
}

interface RuleEditorProps {
    options: ConditionOption[];
    values: any;
    mutators: any;
    beforeForm?: () => React.ReactNode;
    afterForm?: () => React.ReactNode;
    noOptions?: boolean;
}

interface EnhancedMappingEditorProps {
    options: ConditionOption[];
    condition: unknown;
    source: string;
    target: string;
    title: string;
    open: boolean;
    onClose: () => void;
    onChange: (values: any) => void;
    noOptions?: boolean;
    notDialog?: boolean;
    styles?: React.CSSProperties;
    beforeForm?: () => React.ReactNode;
    afterForm?: () => React.ReactNode;
    formValuesRef?: React.MutableRefObject<any>;
}
// endregion

const RecursiveConditional = (props: RecursiveConditionalProps) => {
    const {name, remove, options, depth, mutators, noOptions} = props;
    const form = useForm();

    return (
        <FieldArray name={`${name}.Conditions`}>
            {({fields}: any) => (
                <div style={{
                    flex: 1,
                    padding: fields.length > 0 ? "12px" : "",
                    borderRadius: "6px",
                    border: fields.length > 0 ? "#C0C0C0 solid 1px" : "none",
                    background: fields.length > 0 ? darken("#f5f5f5", depth * 0.05) : "transparent",
                    marginLeft: fields.length > 0 ? `${depth}em` : "0px",
                }}>
                    <>
                        {fields.length > 0 ? (
                                <Grid container style={{flexDirection: "column", gap: pageContentGap}}>
                                    <Grid item container justifyContent={"flex-start"} alignItems={"center"}>
                                        <Field name={`${name}.Logic`}
                                               component={(props: any) => (
                                                   <Typography style={{fontSize: "14px", fontWeight: 700}}>
                                                       {props.input.value.charAt(0).toUpperCase() + props.input.value.slice(1)}
                                                   </Typography>
                                               )}/>
                                    </Grid>

                                    {fields.map((fieldName: string, i: number) =>
                                        <Grid item key={i}>
                                            <RecursiveConditional remove={() => fields.remove(i)} name={fieldName}
                                                                  options={options} depth={depth + 1}
                                                                  mutators={mutators}
                                                                  noOptions={(props as any).noOptions}
                                                                  actions={
                                                                      <Field name={`${name}.Logic`}
                                                                             component={(props: any) => (
                                                                                 <>
                                                                                     <Button variant="outlined" color="primary"
                                                                                         style={{
                                                                                             minWidth: "45px",
                                                                                             maxWidth: "45px",
                                                                                             height: "30px",
                                                                                         }}
                                                                                         onClick={e => {
                                                                                             e.preventDefault();
                                                                                             if (props.input.value === "or")
                                                                                                 fields.push({})
                                                                                             else
                                                                                                 mutators.createOr(name, depth, i);
                                                                                         }}>
                                                                                         Or
                                                                                     </Button>

                                                                                     <Button variant="outlined" color="primary"
                                                                                         style={{
                                                                                             minWidth: "45px",
                                                                                             maxWidth: "45px",
                                                                                             height: "30px",
                                                                                         }}
                                                                                         onClick={e => {
                                                                                             e.preventDefault();
                                                                                             if (props.input.value === "and")
                                                                                                 fields.push({})
                                                                                             else
                                                                                                 mutators.createAnd(name, depth, i);
                                                                                         }}>
                                                                                         And
                                                                                     </Button>
                                                                                 </>
                                                                             )}/>
                                                                  }
                                            />
                                        </Grid>
                                    )}

                                    <Field name={`${name}.Logic`}
                                           component={(props: any) => (
                                               <Button variant="outlined" color="primary"
                                                   style={{width: "80px"}}
                                                   onClick={e => {
                                                       e.preventDefault();
                                                       fields.push({})
                                                   }}>
                                                   {props.input.value === "or" ? (
                                                       <>
                                                           <Nest style={{marginRight: "5px"}}/>
                                                           Or
                                                       </>
                                                   ) : (
                                                       <>
                                                           + And
                                                       </>
                                                   )}
                                               </Button>
                                           )}/>
                                </Grid>
                            )
                            : (
                                <Grid container style={{
                                    flexDirection: "column"
                                }}>
                                    <div className='fieldHolder'>
                                        <Field
                                            component={(props: any) => (
                                                <EnhancedSelect {...props}
                                                                onChange={(e: any) => {
                                                                    props.input.onChange(e.value);
                                                                }}
                                                                value={{
                                                                    label: props.input.value,
                                                                    value: props.input.value
                                                                }}
                                                />
                                            )}
                                            name={`${name}.Field`} label='Field'
                                            options={options}
                                        />

                                        <Field
                                            component={(props: any) => (
                                                <EnhancedSelect {...props}
                                                                onChange={(e: any) => {
                                                                    props.input.onChange(e.value);
                                                                }}
                                                                value={{
                                                                    label: props.input.value,
                                                                    value: props.input.value
                                                                }}
                                                />
                                            )}
                                            name={`${name}.Operator`} label='Comparison'
                                            options={operators}
                                        />

                                        <Field
                                            component={(fieldProps: any) => {
                                                return (
                                                    <>
                                                        {noOptions ? (
                                                            <EnhancedAutocomplete
                                                                {...fieldProps}
                                                                error={fieldProps.meta?.error}
                                                                helperText={fieldProps.meta?.error || 'Please enclose constant values in quotes.'}
                                                                notMultiple={true}
                                                                freeSolo={true}
                                                                options={[]}
                                                                onChange={(e: any, value: string) => {
                                                                    fieldProps.input.onChange(value || '');
                                                                }}
                                                                onInputChange={(e: any, value: string) => {
                                                                    if (e) {
                                                                        fieldProps.input.onChange(value);
                                                                    }
                                                                }}
                                                                value={fieldProps.input.value === 0 ? "" : fieldProps.input.value}
                                                            />
                                                        ) : (
                                                            <EnhancedAutocomplete
                                                                {...fieldProps}
                                                                error={fieldProps.meta?.error}
                                                                helperText={fieldProps.meta?.error || 'Please enclose constant values in quotes.'}
                                                                notMultiple={true}
                                                                onChange={(e: any, value: string) => {
                                                                    fieldProps.input.onChange(value);
                                                                }}
                                                                value={fieldProps.input.value === 0 ? "" : fieldProps.input.value}
                                                                options={options.map(option => option.value)}
                                                            />
                                                        )}
                                                    </>
                                                )
                                            }}
                                            name={`${name}.Value`}
                                            label={"Field expression"}
                                            validate={(v: any) => getErrorMessage(v === null ? undefined : v)}
                                        />

                                        {!props.actions ? (
                                            <IconButton
                                                style={{flex: 0, height: "fit-content", marginTop: "30px"}}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    form.change(`${name}.Field`, undefined);
                                                    form.change(`${name}.Operator`, undefined);
                                                    form.change(`${name}.Value`, undefined);
                                                }}
                                                size="large">
                                                <Clear />
                                            </IconButton>
                                        ) : (
                                            <Grid container justifyContent={"flex-end"} alignItems={"center"}
                                                  style={{
                                                      maxWidth: (depth < 3) ? "140px" : "20px",
                                                      gap: pageContentGap,
                                                      alignItems: "flex-start",
                                                      marginTop: "24px"
                                                  }}
                                            >
                                                {(depth < 3) && props.actions}

                                                {remove &&
                                                    <IconButton
                                                        style={{margin: 0, padding: 0, height: "30px"}}
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            remove();
                                                        }}
                                                        size="large">
                                                        <Delete/>
                                                    </IconButton>
                                                }
                                            </Grid>
                                        )}
                                    </div>

                                    {props.actions !== undefined ? (<></>) : (
                                        <Grid container style={{gap: pageContentGap}}>
                                            <Button variant="outlined" color="primary"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    mutators.createOr(name);
                                                }}>
                                                <Nest style={{marginRight: "5px"}}/> Or
                                            </Button>
                                            <Button variant="outlined" color="primary"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    mutators.createAnd(name);
                                                }}>
                                                + And
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                            )
                        }
                    </>
                </div>
            )}
        </FieldArray>
    );
}

const RuleEditor = (props: RuleEditorProps) => {
    const {noOptions} = props;

    return (
        <Grid container style={{gap: pageContentGap, flexDirection: "column"}}>
            {props.beforeForm && props.beforeForm()}

            {props.beforeForm && (
                <Grid container style={{flexDirection: "column"}}>
                    <hr style={{width: "100%", opacity: "0.2"}}/>
                    <Typography style={{fontSize: "16px", fontWeight: "bold"}}>
                        Conditions
                    </Typography>
                </Grid>
            )}

            <RecursiveConditionField name='Condition' value={props.values.Condition}
                                     options={props.options} mutators={props.mutators}
                                     noOptions={noOptions}
                                     template={(props: any) =>
                                         <RecursiveConditional {...props} />}
            />

            {props.afterForm && props.afterForm()}
        </Grid>
    )
}

export default function EnhancedMappingEditor(props: EnhancedMappingEditorProps) {
    const {options, condition, noOptions} = props;
    const {title, open} = props;

    //region Functions
    const setValues = (values: any) => {
        let cleanedCondition = cleanCondition(values.Condition);
        props.onChange({...values, Condition: cleanedCondition});
    }

    const createAnd = ([name, depth, i]: [string, number | undefined, number], state: any, {changeValue}: any) => {
        changeValue(state, name, (value: any) => {
            if (depth !== undefined) {
                let previousField = value.Field === undefined ? value.Conditions[i].Field : value.Field;
                let previousOperator = value.Operator === undefined ? value.Conditions[i].Operator : value.Operator;
                let previousValue = value.Value === undefined ? value.Conditions[i].Value : value.Value;

                let newConditions = [...value.Conditions];
                newConditions.splice(i, 1, {
                    Logic: 'and',
                    Conditions: [{Field: previousField, Operator: previousOperator, Value: previousValue}, {
                        Field: "",
                        Operator: "",
                        Value: ""
                    }]
                });
                return ({...value, Conditions: newConditions});
            } else {
                return ({Logic: 'and', Conditions: [{...value}, {Field: "", Operator: "", Value: ""}]});
            }
        });
    }

    const createOr = ([name, depth, i]: [string, number | undefined, number], state: any, {changeValue}: any) => {
        changeValue(state, name, (value: any) => {
            if (depth !== undefined) {
                let previousField = value.Field === undefined ? value.Conditions[i].Field : value.Field;
                let previousOperator = value.Operator === undefined ? value.Conditions[i].Operator : value.Operator;
                let previousValue = value.Value === undefined ? value.Conditions[i].Value : value.Value;

                let newConditions = [...value.Conditions];
                newConditions.splice(i, 1, {
                    Logic: 'or',
                    Conditions: [{Field: previousField, Operator: previousOperator, Value: previousValue}, {
                        Field: "",
                        Operator: "",
                        Value: ""
                    }]
                });
                return ({...value, Conditions: newConditions});
            } else {
                return ({Logic: 'or', Conditions: [{...value}, {Field: "", Operator: "", Value: ""}]});
            }
        });
    }
    //endregion

    return (
        <Form
            onSubmit={setValues}
            initialValues={{TargetField: props.target, SourceField: props.source, Condition: condition}}
            mutators={{...arrayMutators, convertToGroup: convertToGroup, createAnd: createAnd, createOr: createOr}}
            render={
                ({handleSubmit, pristine, invalid, values, form: {mutators, reset}}: any) => {
                    if(props.formValuesRef) props.formValuesRef.current = values;
                    return (
                        <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', ...props.styles}}
                              id={"mapping-editor"}>
                            {props.notDialog ? (
                                <RuleEditor
                                    options={options}
                                    values={values}
                                    mutators={mutators}
                                    beforeForm={props.beforeForm}
                                    afterForm={props.afterForm}
                                    noOptions={noOptions}
                                />
                            ) : (
                                <Dialog open={open} onClose={(event, reason) => {
                                  if (reason === 'backdropClick') props.onClose();
                                }}
                                        maxWidth={"md"} fullWidth={true}
                                        slotProps={{
                                            paper: {style: {borderRadius: "12px"}}
                                        }}
                                >
                                    <DialogTitle>
                                        <Typography style={{fontWeight: 700, fontSize: "24px"}}>
                                            {title}
                                        </Typography>
                                    </DialogTitle>

                                    <DialogContent>
                                        <RuleEditor
                                            options={options}
                                            values={values}
                                            mutators={mutators}
                                            beforeForm={props.beforeForm}
                                            afterForm={props.afterForm}
                                            noOptions={noOptions}
                                        />
                                    </DialogContent>

                                    <DialogActions style={{padding: "16px 24px"}}>
                                        <Button variant="outlined"
                                            onClick={() => {
                                                reset({
                                                    TargetField: props.target,
                                                    SourceField: props.source,
                                                    Condition: condition
                                                });
                                                props.onClose();
                                            }}
                                        >
                                            Cancel
                                        </Button>

                                        <Button variant="outlined" color="primary"
                                            disabled={pristine || invalid}
                                            onClick={() => {
                                                setValues(values);
                                                props.onClose();
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            )}
                        </form>
                    );
                }
            }
        />
    );
}
